import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { apiTicketingRedeem } from "../api";
import { Page, Hero, Panel, PButton, C } from "../theme/pbi";

const CDN = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js";
const GATE_KEY = "lusuku_scan_gate";

// Human phrasing per verdict code — the gate crew reads ONE big word plus one
// short line; everything else on the card is for verbal cross-checking.
const VERDICTS = {
  ADMITTED: { word: "ADMIT", ok: true },
  ALREADY_USED: { word: "ALREADY USED", ok: false },
  TOO_EARLY: { word: "WRONG SHOWTIME", ok: false },
  TOO_LATE: { word: "WRONG SHOWTIME", ok: false },
  VOID: { word: "DO NOT ADMIT", ok: false },
  REFUNDED: { word: "DO NOT ADMIT", ok: false },
  INVALID: { word: "DO NOT ADMIT", ok: false },
  NOT_FOUND: { word: "DO NOT ADMIT", ok: false },
};

const minsAgo = (iso) => {
  if (!iso) return null;
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "moments ago";
  if (m < 60) return `${m} min ago`;
  return new Date(iso).toLocaleString();
};

const TicketingScanner = () => {
  const [ready, setReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [gate, setGate] = useState(() => {
    try { return window.localStorage.getItem(GATE_KEY) || ""; } catch (e) { return ""; }
  });
  const scannerRef = useRef(null);
  const lockRef = useRef(false);

  useEffect(() => {
    if (window.Html5Qrcode) { setReady(true); return; }
    const s = document.createElement("script");
    s.src = CDN; s.async = true;
    s.onload = () => setReady(true);
    s.onerror = () => setErr("Could not load the scanner library. Check your connection.");
    document.body.appendChild(s);
  }, []);

  const onGateChange = (v) => {
    setGate(v);
    try { window.localStorage.setItem(GATE_KEY, v); } catch (e) { /* ignore */ }
  };

  const stop = async () => {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); await scannerRef.current.clear(); }
    } catch (e) { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  const onDecoded = async (token) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setBusy(true);
    await stop();
    try {
      const { data } = await apiTicketingRedeem(token, gate.trim() || undefined);
      setResult(data);
    } catch (e) {
      setResult({ ok: false, code: "ERROR", message: e?.response?.data?.message || "Scan failed." });
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setResult(null); setErr(""); lockRef.current = false;
    try {
      const inst = new window.Html5Qrcode("tk-reader");
      scannerRef.current = inst;
      setScanning(true);
      await inst.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 }, (t) => onDecoded(t), () => {});
    } catch (e) {
      setScanning(false);
      setErr("Could not start the camera. Grant camera access and try again.");
    }
  };

  useEffect(() => () => { stop(); }, []);

  const verdict = result ? (VERDICTS[result.code] || { word: "DO NOT ADMIT", ok: false }) : null;
  const admitted = !!(verdict && verdict.ok);
  const verdictColor = admitted ? C.teal : C.accent;
  const showtime = result?.showingStartsAt ? new Date(result.showingStartsAt).toLocaleString() : null;

  return (
    <Page>
      <div style={{ padding: "16px 12px 0" }}>
        <Hero title="Entrance Scanner" subtitle="Point the camera at a customer's ticket QR to admit them. You can only scan tickets for organizers you own." />
        <div style={{ maxWidth: 560 }}>
          <Panel title="Scan a ticket" color={C.teal}>
            {err && <div style={{ background: "#fff4e6", border: "1px solid #ffd8a8", color: "#b45309", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>{err}</div>}

            {/* Gate label — free text, remembered on this device, stamped on every
                scan so the audit trail says WHERE each admit happened. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <label htmlFor="tk-gate" style={{ fontSize: 13, color: C.textMid, whiteSpace: "nowrap", margin: 0 }}>Gate / door</label>
              <input
                id="tk-gate"
                value={gate}
                onChange={(e) => onGateChange(e.target.value)}
                placeholder="e.g. Main gate, Screen 2"
                maxLength={60}
                style={{ flex: 1, border: "1px solid #d8dee4", borderRadius: 8, padding: "8px 10px", fontSize: 13 }}
              />
            </div>

            <div id="tk-reader" style={{ width: "100%", minHeight: scanning ? 300 : 0, borderRadius: 10, overflow: "hidden" }} />

            {result && (
              <div style={{ textAlign: "center", margin: "16px 0", padding: 18, border: `2px solid ${verdictColor}`, borderRadius: 12, background: admitted ? C.tealSoft : "#fff0f3" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: verdictColor }}>{verdict.word}</div>

                {/* Identity block: crew verbally cross-checks event + time + seat. */}
                {result.eventTitle && <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginTop: 6 }}>{result.eventTitle}</div>}
                {showtime && <div style={{ color: C.textMid, fontSize: 13 }}>{showtime}</div>}
                <div style={{ marginTop: 4 }}>
                  {result.seatLabel && <span style={{ fontSize: 15, fontWeight: 700, color: C.text, marginRight: 8 }}>{result.unitLabel && result.unitLabel !== "ticket" ? result.unitLabel : "Seat"} {result.seatLabel}</span>}
                  {result.ticketType && <span style={{ color: C.textMid }}>{result.ticketType}</span>}
                </div>
                {Number(result.admissionsCount || 1) > 1 && <div style={{ fontSize: 18, fontWeight: 800, color: C.green, marginTop: 6 }}>Admit {result.admissionsCount} guests</div>}
                {result.serial && <div style={{ fontFamily: "monospace", fontSize: 12, color: C.textLight, marginTop: 4 }}>{result.serial}</div>}

                {result.code === "ALREADY_USED" && (
                  <div style={{ color: C.textLight, fontSize: 13, marginTop: 6 }}>
                    Scanned {minsAgo(result.redeemedAt) || "earlier"}
                    {result.redeemedGate ? ` · ${result.redeemedGate}` : ""} — if the guest insists this is their ticket, their QR was copied.
                  </div>
                )}
                {(result.code === "TOO_EARLY" || result.code === "TOO_LATE") && (
                  <div style={{ color: C.textLight, fontSize: 13, marginTop: 6 }}>
                    {result.message} {showtime ? `Ticket is for ${showtime}.` : ""}
                  </div>
                )}
                {!admitted && !["ALREADY_USED", "TOO_EARLY", "TOO_LATE"].includes(result.code) && result.message && (
                  <div style={{ color: C.textLight, fontSize: 13, marginTop: 6 }}>{result.message}</div>
                )}
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: 12 }}>
              {busy ? (
                <Spinner animation="border" style={{ color: C.teal }} />
              ) : !scanning ? (
                <PButton disabled={!ready} onClick={start} style={{ background: ready ? "linear-gradient(135deg,#00b69b 0%,#009e88 100%)" : "#b7c4b7" }}>
                  {ready ? (result ? "Scan next" : "Start scanning") : "Loading scanner…"}
                </PButton>
              ) : (
                <PButton variant="outline" onClick={stop} style={{ color: C.teal, borderColor: C.teal }}>Stop scanning</PButton>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </Page>
  );
};

export default TicketingScanner;
